/**
 * Per-form configuration and text helpers for the "Generate with AI" description button.
 *
 * Each field map lists which form values feed the generator (`build`) and the minimum details needed
 * before the API is called (`required`). Only whitelisted, non-sensitive values are ever returned:
 * never phone numbers, emails, exact addresses or ID numbers.
 */

const clean = (value) => {
  if (value === undefined || value === null) return "";
  const text = String(value).trim();
  return text && text !== "null" && text !== "undefined" ? text : "";
};

const compact = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => (Array.isArray(v) ? v.length > 0 : clean(v) !== "")));

const nameOf = (list, id, nameKey = "name") => {
  if (id === undefined || id === null || id === "") return "";
  const match = (list || []).find((item) => String(item.id) === String(id));
  return clean(match?.[nameKey]);
};

const trueKeys = (obj) =>
  Object.entries(obj || {})
    .filter(([key, value]) => value === true && key !== "id")
    .map(([key]) => key.replace(/_/g, " ").trim());

export const AI_TYPES = {
  property: {
    wordRange: [80, 150],
    required: [
      { key: "property_type", label: "property type" },
      { key: "city", label: "city" },
      { key: "price", label: "price" },
    ],
    build: (v = {}) =>
      compact({
        listing_type: v.propertyPurposeName,
        property_type: v.propertyTypeName,
        sub_type: v.propertySubtypeName,
        sub_sub_type: v.propertySubSubTypeName,
        land_type: v.landTypeName,
        title: v.name,
        price: clean(v.price) ? `INR ${v.price}` : "",
        city: v.cityName,
        state: v.stateName,
        locality: v.area,
        bedrooms: v.structure?.bed_rooms,
        bathrooms: v.structure?.bath_rooms,
        rooms: v.structure?.rooms,
        amenities: trueKeys(v.feature),
      }),
  },
  post: {
    wordRange: [40, 80],
    required: [
      { key: "category", label: "service category" },
      { key: "city", label: "city" },
    ],
    build: (v = {}) => compact({ category: v.service, city: v.city_name, state: v.state_name }),
  },
  lease: {
    wordRange: [40, 80],
    required: [
      { key: "title", label: "title" },
      { key: "city", label: "city" },
    ],
    build: (v = {}) =>
      compact({
        title: v.title,
        purpose: v.purposeName,
        budget: clean(v.price) ? `INR ${v.price}` : "",
        city: v.city,
        state: v.state,
      }),
  },
  customer_lead: {
    wordRange: [40, 80],
    required: [
      { key: "title", label: "title" },
      { key: "city", label: "city" },
    ],
    build: (v = {}) =>
      compact({
        title: v.title,
        purpose: v.purposeName,
        budget: clean(v.budget) ? `INR ${v.budget}` : "",
        city: v.city,
        state: v.state,
      }),
  },
  service: {
    wordRange: [40, 80],
    required: [{ key: "title", label: "service title" }],
    build: (v = {}) =>
      compact({ category: v.categoryName, title: v.title, price: clean(v.price) ? `INR ${v.price}` : "" }),
  },
  local_bazaar: {
    wordRange: [40, 80],
    required: [{ key: "title", label: "product title" }],
    build: (v = {}) =>
      compact({
        category: v.categoryName,
        title: v.title,
        price: clean(v.price) ? `INR ${v.price}` : "",
        city: v.city,
        state: v.stateName,
      }),
  },
  interior_design: {
    wordRange: [40, 80],
    required: [{ key: "category", label: "design category" }],
    build: (v = {}) =>
      compact({ category: v.categoryName, budget: clean(v.price) ? `INR ${v.price}` : "" }),
  },
};

export const buildAIFields = (type, values) => AI_TYPES[type]?.build(values) ?? {};

/** Returns the labels of the required details that are still empty. */
export const missingAIFields = (type, fields) =>
  (AI_TYPES[type]?.required ?? []).filter(({ key }) => !fields?.[key]).map(({ label }) => label);

export const missingFieldsMessage = (labels) => {
  if (!labels.length) return "";
  const list =
    labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
  return `Fill in ${list} first to generate a description.`;
};

export { nameOf };

// ---------- text helpers ----------

export const stripHtml = (html) =>
  String(html ?? "")
    .replace(/<\/p>\s*<p>/gi, "\n\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const escapeHtml = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Wraps plain paragraphs for rich-text (Quill) fields. */
export const toHtml = (text) =>
  text
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph.replace(/\n/g, " ").trim())}</p>`)
    .join("");

/** Removes contact details before text leaves the browser. */
export const redactContacts = (text) =>
  String(text ?? "")
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "")
    .replace(/(\+?\d[\d\s-]{8,}\d)/g, "")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

/** Drops markdown/emoji noise and fits the text into maxLength (counting any HTML wrapper). */
export const normalizeGenerated = (raw, { maxLength, format }) => {
  let text = String(raw ?? "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/(\*\*|__|\*|_)(.*?)\1/g, "$2")
    .replace(/^\s*[-•*]\s+/gm, "")
    .replace(/\p{Extended_Pictographic}/gu, "")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  if (!text) return "";
  const fits = (value) => (format === "html" ? toHtml(value) : value).length <= maxLength;
  if (!fits(text)) {
    // Cut at the last sentence end that fits, falling back to a word boundary
    let cut = text;
    while (cut.length > 0 && !fits(cut)) cut = cut.slice(0, Math.max(0, cut.length - Math.max(1, Math.ceil(cut.length * 0.05))));
    const sentenceEnd = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("."));
    text = sentenceEnd > cut.length * 0.5 ? cut.slice(0, sentenceEnd + 1) : cut.replace(/\s+\S*$/, "").trim();
  }
  return text;
};
